/**
 * @file WCH584Upgrader.ts
 * @description 沁恒 (WCH) CH584 固件升级器
 *
 * 基于私有 HID Output Report 协议进行升级。
 * 流程: 判断升级类型 → 进入升级模式 → 更新固件索引 → 擦除Flash → 写入固件(0-50%) → 验证固件(50-100%) → 结束(重启)
 *
 * 与 WCHUpgrader 的差异（保持旧版 WCH584 协议兼容）：
 *   - 发送缓冲区固定 64 字节，数据块大小 0x30 (48 字节)
 *   - 每条命令末尾追加累加校验和字节
 *   - 支持单区 (single) / 双区 (double) 升级模式，由 PID + 连接方式决定
 *   - 绝对地址寻址（起始 0x3000，按 16 字节扇区为单位递增）
 *   - 采用一问一答：发送后等待设备 Input Report 应答 (reportId 0x00)
 *
 * 命令协议:
 *   - CMD_IAP_PROM   (0x80): 写入固件数据块
 *   - CMD_IAP_ERASE  (0x81): 擦除Flash
 *   - CMD_IAP_VERIFY (0x82): 验证固件数据块
 *   - CMD_IAP_END    (0x83): 结束升级
 *   - CMD_IAP_FW_INFO(0x84): 更新/查询固件索引
 *
 * 通信方式:
 *   - HID Output Report ID: 0x00
 *   - 发送缓冲区大小: 64 字节
 *   - 数据块大小: 0x30 (48 字节)
 */

import { BaseDevice } from '../../../../runtime/BaseDevice'
import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import type { HidReportListener } from '../../../../channels/hid/HidChannel'

// 命令常量
const CMD_IAP_PROM = 0x80
const CMD_IAP_ERASE = 0x81
const CMD_IAP_VERIFY = 0x82
const CMD_IAP_END = 0x83
const CMD_IAP_FW_INFO = 0x84

const REPORT_ID = 0x00
// const REPORT_ID = 0x05
const DATA_MAX_LENGTH = 64
const CHUNK_SIZE = 0x30 // 48 bytes per write/verify chunk
const START_ADDRESS = 0x00003000

const RESPONSE_TIMEOUT_MS = 3000
const RESPONSE_POLL_MS = 5
const SEND_PACE_MS = 10
const NO_RESPONSE_DELAY_MS = 50

/** 升级类型枚举 */
export const UPDATE_TYPE = {
  SINGLE: 'single',
  DOUBLE: 'double',
} as const
export type UpdateType = (typeof UPDATE_TYPE)[keyof typeof UPDATE_TYPE]

/** 从字节数组按小端读取 16 位无符号整数 */
function readUint16LE(data: Uint8Array, offset: number): number {
  return data[offset] | (data[offset + 1] << 8)
}

/** 从字节数组按小端读取 32 位无符号整数 */
function readUint32LE(data: Uint8Array, offset: number): number {
  const value =
    data[offset] | (data[offset + 1] << 8) | (data[offset + 2] << 16) | (data[offset + 3] << 24)
  return value >>> 0
}

/** 擦除参数：起始地址以 16 字节扇区为单位，count 为待擦除扇区数 */
interface EraseParams {
  startAddress: number
  count: number
}

export class WCH584Upgrader {
  private device: BaseDevice
  private firmwareData: Uint8Array = new Uint8Array(0)

  private onProgressCallback: ((progress: number) => void) | null = null
  private onProgressFinishCallback: (() => void) | null = null

  /** 当前写入/校验地址（按 16 字节扇区为单位，与旧版逻辑保持一致） */
  private address = 0
  private startAddress = START_ADDRESS
  private updateType: UpdateType = UPDATE_TYPE.SINGLE
  private isUpdating = false
  private runFlag = true

  /** 最近一次 Input Report 应答 (reportId 0x00) */
  private responseBuf: Uint8Array | null = null
  private updateFinished = false

  /** 监听设备 Input Report，捕获 reportId 0x00 的应答数据 */
  private readonly handleInputReport: HidReportListener = (reportId, data) => {
    if (reportId !== REPORT_ID) return
    this.responseBuf = new Uint8Array(
      data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength),
    )
  }

  constructor(device: BaseDevice) {
    this.device = device
    device.channel.onInputReport(this.handleInputReport)
  }

  // ==================== 公共方法 ====================

  setOnProgressCallback(callback: (progress: number) => void) {
    this.onProgressCallback = callback
  }

  setOnProgressFinishCallback(callback: () => void) {
    this.onProgressFinishCallback = callback
  }

  /**
   * 启动 WCH584 固件升级
   * @param firmwareData .bin 固件数据
   * @param updateType 固件升级类型，单区固件升级，双区固件升级
   * @param isDongle 是否是接收器
   */
  async startUpdate(
    firmwareData: Uint8Array,
    updateType: UpdateType,
    isDongle: boolean,
  ): Promise<void> {
    if (this.isUpdating) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'WCH584 升级正在进行中')
    }
    this.updateType = updateType

    this.isUpdating = true
    this.runFlag = true
    this.updateFinished = false
    this.firmwareData = firmwareData

    try {
      // 1. 判断升级类型 (单区/双区)
      // this.determineUpdateType()

      // 2. 进入升级模式: 单区走设备升级检查, 双区走 WCH@TOOL 握手
      if (this.updateType === UPDATE_TYPE.SINGLE) {
        await this.deviceUpdateCheck()
      } else {
        await this.startDeviceUpdate()
      }

      // 3. 更新固件索引
      await this.updateFirmwareIndex(1)

      // 4. 擦除 Flash
      await this.eraseDeviceFlash()

      // 5. 写入固件 (进度 0% → 50%)
      await this.writeDeviceFlashData()

      // 6. 验证固件 (进度 50% → 100%)
      await this.checkDeviceFlashData()
    } catch (error) {
      console.error('[WCH584] 固件升级失败:', error)
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `WCH584 升级失败: ${error instanceof Error ? error.message : JSON.stringify(error)}`,
      )
    } finally {
      this.isUpdating = false
      this.runFlag = false
    }
  }

  /**
   * 结束设备更新 (结束升级 + 按模式决定是否重启)
   */
  async endDeviceUpdate(): Promise<boolean> {
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    sendBuf[0] = CMD_IAP_END
    sendBuf[1] = 0x00
    sendBuf[2] = CMD_IAP_END

    await this.sendDeviceData(sendBuf)

    const response = this.responseBuf
    if (!response || response[2] !== 0x00) {
      throw new DeviceError(
        DeviceErrorCode.WRITE_FAILED,
        `WCH584 设备升级失败, 状态: ${response ? `0x${response[2].toString(16)}` : '无应答'}`,
      )
    }

    this.emitSendUpdateProgress(100)

    // 单区升级结束后需要额外重置设备
    if (this.updateType === UPDATE_TYPE.SINGLE) {
      await this.resetDevice()
    }

    return true
  }

  /**
   * 停止升级
   */
  stopUpgrade() {
    this.runFlag = false
    this.isUpdating = false
  }

  // ==================== 私有方法 ====================
  /**
   * 设备升级检查 (单区)
   * 载荷: [0x5a, 0x02, pid_lo, pid_hi, 校验和]
   */
  private async deviceUpdateCheck(): Promise<void> {
    const pid = this.device.channel.deviceInfo?.productId ?? 0
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    let totalValue = 0

    sendBuf[0] = 0x5a
    sendBuf[1] = 0x02
    sendBuf[2] = pid & 0xff
    sendBuf[3] = (pid >> 8) & 0xff

    for (let i = 0; i < 4; i++) {
      totalValue += sendBuf[i]
    }
    sendBuf[4] = totalValue & 0xff

    await this.sendDeviceData(sendBuf)
  }

  /**
   * 启动设备升级握手 (双区)
   * 载荷: [0x5a, 0x08, "WCH@TOOL", 校验和]
   */
  private async startDeviceUpdate(): Promise<void> {
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    sendBuf[0] = 0x5a
    sendBuf[1] = 0x08

    const str = 'WCH@TOOL'
    const latin1Bytes = new TextEncoder().encode(str)
    sendBuf.set(latin1Bytes, 2)

    let totalValue = 0
    for (let i = 0; i < latin1Bytes.length + 2; i++) {
      totalValue += sendBuf[i]
    }
    sendBuf[10] = totalValue & 0xff

    await this.sendDeviceData(sendBuf)
  }

  /**
   * 更新固件索引
   * 载荷: [0x84, 0x04, index, 0x00, 0x00, 0x00, 校验和]
   */
  private async updateFirmwareIndex(index: number): Promise<void> {
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    let totalValue = 0

    sendBuf[0] = CMD_IAP_FW_INFO
    sendBuf[1] = 0x04
    sendBuf[2] = index

    for (let i = 0; i < 6; i++) {
      totalValue += sendBuf[i]
    }
    sendBuf[6] = totalValue & 0xff

    await this.sendDeviceData(sendBuf)
  }

  /**
   * 擦除设备 Flash
   *
   * 扇区数与起始地址来自上一条命令 (updateFirmwareIndex) 的应答，但**单区与双区的字段布局不同**，
   * 必须按 updateType 分流解析：用同一套偏移解析两种模式会导致起始地址与擦除扇区数同时错位。
   *
   * 单区（对应 QT `FirmwareUpgradeWCH585::eraseDeviceFlashNew`）：
   *   [2..5]  固件起始地址 (LE) → startAddress = address / 16
   *   [6..9]  固件总大小   (LE)
   *   [10..11] 扇区大小    (LE)
   *
   * 双区（对应 QT `FirmwareUpgradeWCH585::eraseDeviceFlash`）：
   *   [2..5]  固件总大小   (LE)
   *   [6..7]  扇区大小     (LE) → startAddress = blockSize / 16
   *
   * 两种模式下 count 均为 floor(固件总大小 / 扇区大小)。
   */
  private async eraseDeviceFlash(): Promise<void> {
    const response = this.responseBuf
    if (!response) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, 'WCH584 擦除前未获取到有效的固件信息应答')
    }

    const { startAddress, count } =
      this.updateType === UPDATE_TYPE.SINGLE
        ? this.parseSingleAreaEraseParams(response)
        : this.parseDoubleAreaEraseParams(response)

    this.startAddress = startAddress
    await this.sendEraseCommand(startAddress, count)
  }

  /**
   * 解析单区固件信息应答: [2..5]=起始地址, [6..9]=固件总大小, [10..11]=扇区大小
   */
  private parseSingleAreaEraseParams(response: Uint8Array): EraseParams {
    if (response.length < 12) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `WCH584 单区擦除前应答长度不足 (${response.length} < 12)`,
      )
    }

    const address = readUint32LE(response, 2)
    const totalSize = readUint32LE(response, 6)
    const blockSize = readUint16LE(response, 10)

    return {
      startAddress: Math.floor(address / 16),
      count: this.calcEraseCount(totalSize, blockSize, '单区'),
    }
  }

  /**
   * 解析双区固件信息应答: [2..5]=固件总大小, [6..7]=扇区大小
   */
  private parseDoubleAreaEraseParams(response: Uint8Array): EraseParams {
    if (response.length < 8) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `WCH584 双区擦除前应答长度不足 (${response.length} < 8)`,
      )
    }

    const totalSize = readUint32LE(response, 2)
    const blockSize = readUint16LE(response, 6)

    return {
      startAddress: Math.floor(blockSize / 16),
      count: this.calcEraseCount(totalSize, blockSize, '双区'),
    }
  }

  /** 计算擦除扇区数；扇区大小为 0 说明应答无效，直接报错而不是产出 Infinity/NaN */
  private calcEraseCount(totalSize: number, blockSize: number, area: string): number {
    if (!blockSize) {
      throw new DeviceError(
        DeviceErrorCode.READ_FAILED,
        `WCH584 ${area}擦除前应答中扇区大小无效: ${blockSize}`,
      )
    }
    return Math.floor(totalSize / blockSize)
  }

  /**
   * 下发擦除命令
   * 载荷: [0x81, 0x04, addr_lo, addr_hi, count_lo, count_hi, 校验和]
   */
  private async sendEraseCommand(startAddress: number, count: number): Promise<void> {
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    let totalValue = 0

    sendBuf[0] = CMD_IAP_ERASE
    sendBuf[1] = 0x04
    sendBuf[2] = startAddress & 0xff
    sendBuf[3] = (startAddress >> 8) & 0xff
    sendBuf[4] = count & 0xff
    sendBuf[5] = (count >> 8) & 0xff

    for (let i = 0; i < 6; i++) {
      totalValue += sendBuf[i]
    }
    sendBuf[6] = totalValue & 0xff

    await this.sendDeviceData(sendBuf)
  }

  /**
   * 写入固件数据到设备 (进度 0% → 50%)
   */
  private async writeDeviceFlashData(): Promise<boolean> {
    if (!this.firmwareData || this.firmwareData.length === 0) return false

    let dataLength = 0
    const totalLength = this.firmwareData.length
    this.address = this.startAddress

    while (dataLength < totalLength && this.runFlag) {
      const writeLength = Math.min(CHUNK_SIZE, totalLength - dataLength)
      await this.writeSingleChunk(dataLength, writeLength)
      dataLength += writeLength

      // 更新进度: 0% → 50%
      const percent = Math.floor((dataLength * 100) / totalLength / 2)
      this.emitSendUpdateProgress(percent)

      // 小延迟避免过快发送
      await this.msleep(SEND_PACE_MS)
    }

    return true
  }

  /**
   * 写入单个数据块
   * 载荷: [0x80, len, addr_lo, addr_hi, ...data, 校验和]
   */
  private async writeSingleChunk(offset: number, chunkSize: number): Promise<void> {
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    let totalValue = 0

    sendBuf[0] = CMD_IAP_PROM
    sendBuf[1] = chunkSize + 2 // +2 for address bytes
    sendBuf[2] = this.address & 0xff
    sendBuf[3] = (this.address >> 8) & 0xff
    sendBuf.set(this.firmwareData.subarray(offset, offset + chunkSize), 4)

    for (let i = 0; i < chunkSize + 4; i++) {
      totalValue += sendBuf[i]
    }
    sendBuf[chunkSize + 4] = totalValue & 0xff

    const response = await this.sendDeviceData(sendBuf)
    this.address += chunkSize / 16
    this.verifyResponseStatus(response)
  }

  /**
   * 验证固件数据 (进度 50% → 100%)
   */
  private async checkDeviceFlashData(): Promise<boolean> {
    if (!this.firmwareData || this.firmwareData.length === 0) return false

    let dataLength = 0
    const totalLength = this.firmwareData.length
    this.address = this.startAddress

    while (dataLength < totalLength && this.runFlag) {
      const verifyLength = Math.min(CHUNK_SIZE, totalLength - dataLength)
      await this.verifySingleChunk(dataLength, verifyLength)
      dataLength += verifyLength

      // 更新进度: 50% → 100%
      const percent = 50 + Math.floor((dataLength * 100) / totalLength / 2)
      this.emitSendUpdateProgress(percent)

      await this.msleep(SEND_PACE_MS)
    }

    return true
  }

  /**
   * 校验单个数据块
   * 载荷: [0x82, len, addr_lo, addr_hi, ...data, 校验和]
   */
  private async verifySingleChunk(offset: number, chunkSize: number): Promise<void> {
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    let totalValue = 0

    sendBuf[0] = CMD_IAP_VERIFY
    sendBuf[1] = chunkSize + 2 // +2 for address bytes
    sendBuf[2] = this.address & 0xff
    sendBuf[3] = (this.address >> 8) & 0xff
    sendBuf.set(this.firmwareData.subarray(offset, offset + chunkSize), 4)

    for (let i = 0; i < chunkSize + 4; i++) {
      totalValue += sendBuf[i]
    }
    sendBuf[chunkSize + 4] = totalValue & 0xff

    const response = await this.sendDeviceData(sendBuf)
    this.address += chunkSize / 16
    this.verifyResponseStatus(response)
  }

  /**
   * 重置设备 (单区升级结束后)
   * 载荷: [0x00, 0x84, 0x00]
   */
  private async resetDevice(): Promise<void> {
    const sendBuf = new Uint8Array(DATA_MAX_LENGTH)
    sendBuf[0] = 0x00
    sendBuf[1] = CMD_IAP_FW_INFO
    sendBuf[2] = 0x00
    await this.sendDeviceDataNoResponse(sendBuf)
  }

  /**
   * 通过 Output Report 发送数据到设备
   */
  private async sendReport(data: Uint8Array): Promise<void> {
    const buf = new Uint8Array(data)
    await this.device.channel.sendOutputReport(REPORT_ID, buf)
  }

  /**
   * 发送设备数据并等待应答
   */
  private async sendDeviceData(data: Uint8Array): Promise<Uint8Array> {
    this.responseBuf = null
    await this.sendReport(data)
    return this.waitForResponse()
  }

  /**
   * 发送设备数据 (无需应答)
   */
  private async sendDeviceDataNoResponse(data: Uint8Array): Promise<void> {
    await this.sendReport(data)
    await this.msleep(NO_RESPONSE_DELAY_MS)
  }

  /**
   * 等待设备应答 (轮询 Input Report)
   */
  private async waitForResponse(): Promise<Uint8Array> {
    const startTime = Date.now()
    while (!this.responseBuf) {
      if (Date.now() - startTime > RESPONSE_TIMEOUT_MS) {
        throw new DeviceError(DeviceErrorCode.TIMEOUT, 'WCH584 等待设备响应超时')
      }
      await this.msleep(RESPONSE_POLL_MS)
    }
    return this.responseBuf
  }

  /**
   * 校验设备应答状态
   */
  private verifyResponseStatus(response: Uint8Array): void {
    if (!response || response.length < 3 || response[2] !== 0x00) {
      const status = response && response.length > 2 ? `0x${response[2].toString(16)}` : '无效应答'
      throw new DeviceError(DeviceErrorCode.WRITE_FAILED, `WCH584 设备错误: ${status}`)
    }
  }

  private msleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private emitSendUpdateProgress(percent: number) {
    const p = Math.min(100, Math.max(0, Math.floor(percent)))
    if (this.onProgressCallback) {
      this.onProgressCallback(p)
      if (p === 100) {
        this.emitUpdateFinished()
      }
    }
  }

  private emitUpdateFinished() {
    if (this.updateFinished) return
    this.updateFinished = true
    if (this.onProgressFinishCallback) {
      this.onProgressFinishCallback()
    }
  }
}
